/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * One role: what it can do, and who holds it.
 *
 * Reached from the roles list. Full width, because the permission grid needs
 * it and the list does not.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings,
  ArrowBackRounded,
  DeleteOutline,
  PersonAddAlt1Rounded,
  SaveRounded,
  SwapHorizRounded,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useCapability } from 'auth/usePermission';
import { notifyPermissionsChanged } from 'auth/PermissionsProvider';
import StyledAccessDenied from 'components/StyledAccessDenied';

import PermissionGrid from './PermissionGrid';
import { useTheme } from "@mui/material/styles";

const displayName = (u) =>
  `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username || u.email;

const initials = (u) =>
  displayName(u).split(' ').filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join('');

export default function RoleDetail() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const canManage = useCapability('MANAGE_ROLES');

  const [role, setRole] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  // Which member's "Move to..." menu is open, and where to anchor it.
  const [moveMenu, setMoveMenu] = useState(null);
  const [grid, setGrid] = useState(null);
  const [dirty, setDirty] = useState({});
  const [tab, setTab] = useState(0);
  const [members, setMembers] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rolesRes, gridRes] = await Promise.all([
        axiosInstance.get('/roles'),
        axiosInstance.get(`/roles/${roleId}/permissions`),
      ]);
      setAllRoles(rolesRes.data);
      setRole(rolesRes.data.find((r) => String(r.id) === String(roleId)) ?? null);
      setGrid(gridRes.data);
      setDirty({});
    } catch {
      setToast({ severity: 'error', message: 'Could not load this role.' });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, roleId]);

  const loadMembers = useCallback(async () => {
    try {
      const [assigned, available] = await Promise.all([
        axiosInstance.get(`/roles/${roleId}/users`),
        axiosInstance.get(`/roles/${roleId}/assignable-users`),
      ]);
      setMembers(assigned.data);
      setAssignable(available.data);
    } catch {
      setMembers([]);
      setAssignable([]);
    }
  }, [axiosInstance, roleId]);

  useEffect(() => { load(); loadMembers(); }, [load, loadMembers]);

  const changeObject = useCallback((apiName, patch) => {
    setGrid((current) => current && ({
      ...current,
      groups: current.groups.map((g) => ({
        ...g,
        objects: g.objects.map((o) => (o.api_name === apiName ? { ...o, ...patch } : o)),
      })),
    }));
    setDirty((current) => ({ ...current, [apiName]: true }));
  }, []);

  const changeGroup = useCallback((group, patch) => {
    setGrid((current) => current && ({
      ...current,
      groups: current.groups.map((g) =>
        g.code === group.code
          ? { ...g, objects: g.objects.map((o) => ({ ...o, ...patch })) }
          : g
      ),
    }));
    setDirty((current) => {
      const next = { ...current };
      group.objects.forEach((o) => { next[o.api_name] = true; });
      return next;
    });
  }, []);

  const save = async () => {
    if (!grid) return;
    const changed = grid.groups
      .flatMap((g) => g.objects)
      .filter((o) => dirty[o.api_name])
      .map((o) => ({
        object_api_name: o.api_name,
        grant_level: o.grant_level,
        record_scope: o.record_scope,
        write_scope: o.write_scope ?? null,
      }));
    if (!changed.length) return;

    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`/roles/${roleId}/permissions`, { grants: changed });
      setGrid(data);
      setDirty({});
      notifyPermissionsChanged();
      setToast({ severity: 'success', message: `Saved ${changed.length} change(s).` });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setToast({
        severity: 'error',
        message: typeof detail === 'string' ? detail : 'Could not save changes.',
      });
    } finally {
      setSaving(false);
    }
  };

  const assignUser = async (user) => {
    if (!user) return;
    try {
      await axiosInstance.post(`/roles/${roleId}/users`, { user_ids: [user.id] });
      await loadMembers();
      notifyPermissionsChanged();
    } catch {
      setToast({ severity: 'error', message: 'Could not assign that person.' });
    }
  };

  const removeUser = async (userId) => {
    try {
      await axiosInstance.delete(`/roles/${roleId}/users/${userId}`);
      await loadMembers();
      notifyPermissionsChanged();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setToast({
        severity: 'error',
        message: detail?.message ?? 'Could not remove that person.',
      });
    }
  };

  /**
   * Taking someone off a base role means giving them a different one.
   *
   * Everyone holds exactly one base role -- a partial unique index enforces it
   * -- so there is no such thing as removing the last one. Assigning the new
   * role replaces the old in a single call, which is what the delete button
   * used to promise and the server refused with BASE_ROLE_REQUIRED.
   */
  const moveToBaseRole = async (userId, targetRoleId) => {
    setMoveMenu(null);
    try {
      await axiosInstance.post(`/roles/${targetRoleId}/users`, { user_ids: [userId] });
      await loadMembers();
      notifyPermissionsChanged();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setToast({
        severity: 'error',
        message: detail?.message ?? 'Could not move that person.',
      });
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <StyledAccessDenied />
      </DashboardLayout>
    );
  }

  const dirtyCount = Object.keys(dirty).length;
  const isBaseRole = role?.role_kind === 'base';
  // Excludes this role for obvious reasons, and super roles because moving
  // someone into one is granting full access -- that belongs on the
  // Administrator role's own People tab, not behind a "move" affordance.
  const otherBaseRoles = allRoles.filter(
    (r) => r.role_kind === 'base' && String(r.id) !== String(roleId) && !r.is_super
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={isV2 ? 0 : 3}>
        <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
          {/* v2: the breadcrumb and the settings menu lead back. */}
          {!isV2 && (
            <IconButton size="small" onClick={() => navigate('/settings/roles')}>
              <ArrowBackRounded />
            </IconButton>
          )}
          <Avatar
            sx={{
              width: 40, height: 40, fontSize: 16, fontWeight: 700,
              bgcolor: role?.is_super ? 'warning.main' : 'rgba(21,101,200,0.10)',
              color: role?.is_super ? '#fff' : 'info.main',
            }}
          >
            {role?.is_super ? <AdminPanelSettings /> : (role?.name?.[0] ?? '?')}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" fontWeight={700} noWrap>
                {role?.name ?? 'Role'}
              </Typography>
              {role && (
                <Chip
                  size="small"
                  label={role.role_kind === 'addon' ? 'Add-on role' : 'Base role'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {role?.description || 'No description'}
            </Typography>
          </Box>
          {tab === 0 && dirtyCount > 0 && (
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="body2" color="text.secondary">
                {dirtyCount} unsaved
              </Typography>
              <MDButton variant="text" color="dark" size="small" onClick={load}>
                Discard
              </MDButton>
              <MDButton
                variant="gradient" color="info" size="small"
                disabled={saving} onClick={save}
              >
                <SaveRounded sx={{ mr: 0.75, fontSize: 18 }} />
                {saving ? 'Saving…' : 'Save changes'}
              </MDButton>
            </Box>
          )}
        </Box>

        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ mb: 2.5 }}
        >
          <Tab label="Permissions" />
          <Tab label={`People (${members.length})`} />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : tab === 0 ? (
          grid && (
            <PermissionGrid
              groups={grid.groups}
              readOnly={Boolean(role?.is_super)}
              onChange={changeObject}
              onGroupChange={changeGroup}
            />
          )
        ) : (
          <Box maxWidth={640}>
            <Autocomplete
              size="small"
              options={assignable}
              getOptionLabel={displayName}
              onChange={(_e, value) => assignUser(value)}
              value={null}
              blurOnSelect
              renderInput={(params) => (
                <TextField {...params} placeholder="Add someone to this role" />
              )}
              sx={{ maxWidth: 400, mb: 1.5 }}
            />
            {isBaseRole && (
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                This is a base role, so assigning it replaces whatever base role the
                person has now. Everyone holds exactly one, which is why people are
                moved to another base role here rather than removed.
              </Typography>
            )}

            <Box display="grid" gap={0.5}>
              {members.map((m) => (
                <Box
                  key={m.user_id}
                  display="flex" alignItems="center" gap={1.5}
                  sx={{
                    px: 1.5, py: 1,
                    border: '1px solid', borderColor: 'divider', borderRadius: '10px',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700 }}>
                    {initials(m)}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>{displayName(m)}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {m.email || m.username}
                    </Typography>
                  </Box>
                  {isBaseRole ? (
                    <Tooltip title={
                      otherBaseRoles.length
                        ? 'Move to another base role'
                        : 'There is no other base role to move this person to'
                    }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!otherBaseRoles.length}
                          onClick={(e) => setMoveMenu({
                            userId: m.user_id,
                            anchorEl: e.currentTarget,
                          })}
                        >
                          <SwapHorizRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Remove from this role">
                      <span>
                        <IconButton size="small" onClick={() => removeUser(m.user_id)}>
                          <DeleteOutline sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              ))}
              <Menu
                open={Boolean(moveMenu)}
                anchorEl={moveMenu?.anchorEl ?? null}
                onClose={() => setMoveMenu(null)}
              >
                {otherBaseRoles.map((r) => (
                  <MenuItem
                    key={r.id}
                    onClick={() => moveToBaseRole(moveMenu.userId, r.id)}
                  >
                    {r.name}
                  </MenuItem>
                ))}
              </Menu>
              {!members.length && (
                <Box textAlign="center" py={5}>
                  <PersonAddAlt1Rounded sx={{ fontSize: 34, opacity: 0.25 }} />
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Nobody has this role yet.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </MDBox>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
